import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuArrowRight, LuBell, LuCalendarDays, LuCalendarCheck, LuClock, LuMessageSquare, LuTrash2 } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { getInitials } from '../../components/ui/Avatar';
import { GLASS_CHIP } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import { leaveTypeLabel } from './employeeForm/employeeFormOptions';

const toDate = (value) => (value && typeof value.toDate === 'function' ? value.toDate() : new Date(value));

/**
 * "Bekleyen İzin Talepleri". Bir talebe tıklamak o çalışanı tabloda seçer ve
 * satırına odaklanır; silme bir kez daha onay ister.
 */
const PendingLeavesModal = ({ leaves, onClose, onFocusEmployee, onDelete }) => {
  const { t, language, locale } = useTranslation();
  const [confirmingId, setConfirmingId] = useState(null);
  const formatDay = (value) => toDate(value).toLocaleDateString(locale);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t('employees.modals.pendingLeaves.title')}
      subtitle={leaves.length ? t('employees.modals.pendingLeaves.subtitle', { count: leaves.length }) : undefined}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuBell aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={<Button variant="ghost" onClick={() => onClose()}>{t('common.close')}</Button>}
    >
      {leaves.length === 0 ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
          <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-emerald-500/10 tw-text-emerald-500">
            <LuCalendarCheck aria-hidden="true" className="tw-h-6 tw-w-6" />
          </span>
          <p className="tw-text-sm tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{t('employees.modals.pendingLeaves.none')}</p>
        </div>
      ) : (
        <ul className="tw-space-y-2.5">
          {leaves.map((leave) => {
            const employee = leave.employee;
            const name = employee
              ? `${employee.eName || ''} ${employee.eSurname || ''}`.trim()
              : t('employees.modals.pendingLeaves.unknownEmployee');
            const confirming = confirmingId === leave.id;
            return (
              <li
                key={leave.id}
                // O çalışanı seç ve odaklan (eskisi gibi kartın tamamı tıklanır)
                onClick={() => {
                  if (employee && !confirming) onFocusEmployee(employee);
                }}
                title={employee ? t('employees.modals.pendingLeaves.focusHint') : undefined}
                className={`tw-rounded-2xl tw-p-3.5 tw-transition-shadow ${GLASS_CHIP} ${employee ? 'tw-cursor-pointer hover:tw-shadow-md' : ''}`}
              >
                <div className="tw-flex tw-items-start tw-gap-3">
                  <span
                    aria-hidden="true"
                    className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
                  >
                    {employee ? getInitials(employee.eName, employee.eSurname) : '?'}
                  </span>
                  <div className="tw-min-w-0 tw-flex-1">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{name}</p>
                      <Badge tone="info">{leaveTypeLabel(t, leave.leaveType)}</Badge>
                    </div>
                    <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                      <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                        <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {formatDay(leave.startDate)} – {formatDay(leave.endDate)}
                      </span>
                      <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                        <LuClock aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {t('employees.modals.pendingLeaves.days', { count: Number(leave.leaveDays) || 0 })}
                      </span>
                    </div>
                    {leave.reason && (
                      <p className="tw-mt-2 tw-flex tw-items-start tw-gap-1.5 tw-text-sm tw-leading-snug tw-text-slate-700 dark:tw-text-slate-200">
                        <LuMessageSquare aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                        <span className="tw-min-w-0 tw-break-words">{leave.reason}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-3 dark:tw-border-white/10">
                  {confirming ? (
                    <>
                      <span className="tw-mr-auto tw-text-xs tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400">
                        {t('employees.modals.pendingLeaves.confirmDelete')}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmingId(null);
                        }}
                      >
                        {t('employees.modals.pendingLeaves.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmingId(null);
                          onDelete(leave.id);
                        }}
                      >
                        <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {t('employees.modals.pendingLeaves.delete')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        title={t('employees.modals.pendingLeaves.deleteHint')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmingId(leave.id);
                        }}
                        className="tw-mr-auto tw-text-rose-600 dark:tw-text-rose-400"
                      >
                        <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {t('employees.modals.pendingLeaves.delete')}
                      </Button>
                      {employee && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFocusEmployee(employee);
                          }}
                        >
                          {t('employees.modals.pendingLeaves.focus')}
                          <LuArrowRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
};

PendingLeavesModal.propTypes = {
  leaves: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onFocusEmployee: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PendingLeavesModal;
